import { Inject, Injectable } from '@angular/core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import Dexie from 'dexie';
import { defer, firstValueFrom, from, Observable, of, switchMap } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import { CachedPayload } from '../../classes/cached-request';
import { OfflineStrategy } from '../../interfaces/offline-strategy.interface';
import { PaceOfflineFeatureState } from '../../store/state';
import { EncryptionService } from '@evorait/pace-encryption';
import { IndexeddbOfflineState } from './indexeddb-offline-strategy.interface';

@Injectable()
export class IndexeddbOfflineStrategyService extends Dexie implements OfflineStrategy {
    public static readonly DB_NAME = 'OfflineCache';
    public static readonly DB_TABLE = 'state';
    public static readonly DB_KEY_PATH = 'id';

    private static readonly VERSION_ONE = 1;
    private static readonly VERSION_TWO = IndexeddbOfflineStrategyService.VERSION_ONE + 1;

    private state: Dexie.Table<IndexeddbOfflineState, number>;
    public constructor(
        @Inject(LogInstanceToken) private log: LogInterface,
        private readonly encryption: EncryptionService,
    ) {
        super(IndexeddbOfflineStrategyService.DB_NAME);
        this.version(IndexeddbOfflineStrategyService.VERSION_ONE).stores({
            [IndexeddbOfflineStrategyService.DB_TABLE]: IndexeddbOfflineStrategyService.DB_KEY_PATH,
        });

        // encrypt potentially existing data
        this.version(IndexeddbOfflineStrategyService.VERSION_TWO).upgrade(async (trans) => {
            await firstValueFrom(this.encryption.selectInitialized.pipe(filter(Boolean)));

            return trans
                .table(IndexeddbOfflineStrategyService.DB_TABLE)
                .toCollection()
                .modify(async (state: IndexeddbOfflineState) => {
                    try {
                        const stringifiedState = state.stringifiedState;
                        if (!stringifiedState.startsWith(EncryptionService.PREFIX_ENCRYPTED_CONTENT)) {
                            state.stringifiedState = this.encryption.encrypt(stringifiedState);
                        }
                    } catch {
                        console.error(
                            `IndexeddbOfflineStrategyService (DB version upgrade): Failed to upgrade state ${state.id} to version ${IndexeddbOfflineStrategyService.VERSION_TWO}`,
                        );
                    }
                });
        });
    }
    public getState(): Observable<PaceOfflineFeatureState | undefined> {
        return defer(() => this.encryption.selectInitialized).pipe(
            filter(Boolean),
            switchMap(() => from(this.state.toArray())),
            map(([state]) => {
                if (!state?.stringifiedState) {
                    return undefined;
                } else {
                    return JSON.parse(this.encryption.decrypt(state.stringifiedState)) as PaceOfflineFeatureState;
                }
            }),
            catchError((e) => {
                this.log.error(
                    `IndexeddbOfflineStrategyService: getState: error reading state from indexeddb: ${JSON.stringify(
                        e,
                    )}`,
                );
                return of(undefined);
            }),
        );
    }

    public setState(state: PaceOfflineFeatureState): Observable<boolean> {
        return defer(() =>
            from(
                this.state.put({
                    id: 0,
                    stringifiedState: this.encryption.encrypt(JSON.stringify(state)),
                }),
            ).pipe(
                tap(() => {
                    this.log.info(
                        `IndexeddbOfflineStrategyService: setState: total ${state.requestQueue.requests.length} pending requests`,
                    );
                    state.requestQueue.requests.forEach((request) =>
                        this.log.info(
                            `${request.id} ${
                                request.url.toLowerCase().indexOf('deepinsert') > 0
                                    ? (<CachedPayload>request.body)?.Endpoint
                                    : request.url
                            }`,
                        ),
                    );
                }),
                map(() => true),
                catchError((e) => {
                    this.log.error(
                        `IndexeddbOfflineStrategyService: setState: error writing state in indexeddb: ${JSON.stringify(
                            e,
                        )}`,
                    );
                    return of(false);
                }),
            ),
        );
    }

    public deleteCache(): Observable<boolean> {
        return defer(() =>
            from(this.state.clear()).pipe(
                tap(() => this.log.debug(`IndexeddbOfflineStrategyService: deleteCache: success`)),
                map(() => true),
                catchError((e) => {
                    this.log.error(
                        `IndexeddbOfflineStrategyService: deleteCache: error deleting state in indexeddb: ${JSON.stringify(
                            e,
                        )}`,
                    );
                    return of(false);
                }),
            ),
        );
    }
}
